import React, { useState, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { apiService, formatItemName, consolidateOrderItems, type OrderItem } from '../services/api'
import {
  MagnifyingGlassIcon,
  EyeIcon,
  PrinterIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline'

interface Invoice {
  id: string
  invoice_type: 'bill_request' | 'pos_receipt' | 'catering_invoice' | 'takeaway_receipt'
  invoice_number: string
  invoice_date: string
  order_id: string
  table_id?: string
  requested_at?: string
  invoice_status?: 'pending' | 'duplicate_generated' | 'final_generated'
  duplicate_invoice_number?: string
  duplicate_invoice_generated_at?: string
  final_invoice_number?: string
  final_invoice_generated_at?: string
  status?: string
  payment_status: string
  payment_method?: string
  paid_amount?: number
  change_amount?: number
  total_amount: number
  orders?: {
    id: string
    order_number: string
    table_number?: string
    customer_name?: string
    customer_phone?: string
    subtotal: number
    tax_amount: number
    service_charge: number
    discount_amount: number
    total_amount: number
    payment_status: string
    payment_method?: string
    paid_amount?: number
    change_amount?: number
    created_at: string
    order_items?: OrderItem[]
  }
  tables?: {
    id: string
    number: string
    capacity?: number
    status: string
  }
}

const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(false)
  const [_printing, setPrinting] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [printModalMessage, setPrintModalMessage] = useState('')
  const [printModalType, setPrintModalType] = useState<'success' | 'error'>('success')
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    start_date: '',
    end_date: '',
    search: ''
  })

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const totalPages = Math.max(1, Math.ceil(invoices.length / pageSize))
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = Math.min(startIndex + pageSize, invoices.length)
  const paginatedInvoices = invoices.slice(startIndex, endIndex)

  // Reset to page 1 when filters or data change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters, invoices.length])

  useEffect(() => {
    loadInvoices()
  }, [filters])

  const loadInvoices = async () => {
    setLoading(true)
    try {
      const response = await apiService.getInvoices(filters)
      if (response.success && response.data) {
        setInvoices(response.data as Invoice[])
      }
    } catch (error) {
      console.error('Error loading invoices:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) {
      return 'SAR 0.00'
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(numAmount)) {
      return 'SAR 0.00'
    }
    return `SAR ${numAmount.toFixed(2)}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getInvoiceStatusLabel = (invoice: Invoice): string => {
    if (invoice.invoice_type === 'catering_invoice') return 'Catering Invoice'
    if (invoice.invoice_type === 'takeaway_receipt') return 'Takeaway Receipt'
    if (invoice.invoice_type === 'pos_receipt') return 'POS Receipt'
    if (invoice.invoice_status === 'final_generated' || invoice.invoice_type === 'bill_request') return 'Final Invoice'
    return 'Pending'
  }

  const getInvoiceStatusBadge = (invoice: Invoice) => {
    if (invoice.invoice_type === 'catering_invoice') {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
          Catering Invoice
        </span>
      )
    } else if (invoice.invoice_type === 'takeaway_receipt') {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200">
          Takeaway Receipt
        </span>
      )
    } else if (invoice.invoice_type === 'pos_receipt') {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
          POS Receipt
        </span>
      )
    } else if (invoice.invoice_status === 'final_generated' || invoice.invoice_type === 'bill_request') {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          Final Invoice
        </span>
      )
    } else {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
          Pending
        </span>
      )
    }
  }

  const exportToExcel = () => {
    const headers = ['Invoice #', 'Order #', 'Table', 'Customer', 'Amount', 'Status', 'Date']
    const rows = invoices.map((inv) => [
      inv.invoice_number || '-',
      inv.orders?.order_number || '-',
      inv.tables?.number || inv.orders?.table_number || '-',
      inv.orders?.customer_name || 'Guest',
      formatCurrency(inv.total_amount ?? inv.orders?.total_amount ?? 0),
      getInvoiceStatusLabel(inv),
      formatDate(inv.invoice_date || inv.requested_at || inv.orders?.created_at || '')
    ])
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Invoices')
    const dateStr = new Date().toISOString().split('T')[0]
    XLSX.writeFile(wb, `Invoices_${dateStr}.xlsx`)
  }

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice)
    setShowInvoiceModal(true)
  }

  const handlePrintInvoice = async (invoice: Invoice) => {
    try {
      setPrinting(true)
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/invoices/${invoice.id}/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invoice_type: invoice.invoice_type
        })
      })
      
      const result = await response.json()
      
      if (result.success) {
        setPrintModalMessage('Invoice sent to printer successfully')
        setPrintModalType('success')
        setShowPrintModal(true)
      } else {
        setPrintModalMessage(`Failed to print invoice: ${result.error || 'Unknown error'}`)
        setPrintModalType('error')
        setShowPrintModal(true)
      }
    } catch (error) {
      console.error('Error printing invoice:', error)
      setPrintModalMessage('Failed to print invoice. Please check printer configuration.')
      setPrintModalType('error')
      setShowPrintModal(true)
    } finally {
      setPrinting(false)
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Invoices
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            View and manage all generated invoices
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by invoice #, order #, customer, table..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Invoice Type
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
            >
              <option value="all">All Invoices</option>
              <option value="pos_receipt">POS Receipts</option>
              <option value="takeaway_receipt">Takeaway Receipts</option>
              <option value="catering_invoice">Catering Invoices</option>
              <option value="final_generated">Final Invoices</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={exportToExcel}
            disabled={invoices.length === 0}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md flex items-center gap-2"
            title="Export to Excel"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            Export to Excel
          </button>
          <button
            onClick={() => setFilters({ status: 'all', start_date: '', end_date: '', search: '' })}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Table</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  paginatedInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {invoice.invoice_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {invoice.orders?.order_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {invoice.tables?.number || invoice.orders?.table_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {invoice.orders?.customer_name || 'Guest'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {formatCurrency(invoice.total_amount || invoice.orders?.total_amount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getInvoiceStatusBadge(invoice)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(invoice.invoice_date || invoice.requested_at || invoice.orders?.created_at || '')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleViewInvoice(invoice)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400"
                            title="View Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handlePrintInvoice(invoice)}
                            className="text-gray-600 hover:text-gray-900 dark:text-gray-400"
                            title="Print Invoice"
                          >
                            <PrinterIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Pagination */}
            {invoices.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <span>
                    Showing {startIndex + 1}–{endIndex} of {invoices.length}
                  </span>
                  <label className="flex items-center gap-2">
                    <span>Rows per page</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value))
                        setCurrentPage(1)
                      }}
                      className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      {[10, 25, 50, 100].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    className="p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                    aria-label="Previous page"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                    aria-label="Next page"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Invoice Details
                </h3>
                <button
                  onClick={() => {
                    setShowInvoiceModal(false)
                    setSelectedInvoice(null)
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Invoice Number</label>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {selectedInvoice.invoice_number || selectedInvoice.final_invoice_number || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Invoice Type</label>
                  <div className="mt-1">
                    {getInvoiceStatusBadge(selectedInvoice)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Order Number</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedInvoice.orders?.order_number || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Table</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedInvoice.tables?.number || selectedInvoice.orders?.table_number || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Customer</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedInvoice.orders?.customer_name || 'Guest'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Invoice Date</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {formatDate(selectedInvoice.invoice_date || selectedInvoice.requested_at || selectedInvoice.orders?.created_at || '')}
                  </p>
                </div>
                {selectedInvoice.final_invoice_number && selectedInvoice.invoice_type === 'bill_request' && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Final Invoice #</label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedInvoice.final_invoice_number}
                    </p>
                    {selectedInvoice.final_invoice_generated_at && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(selectedInvoice.final_invoice_generated_at)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Order Items */}
              {selectedInvoice.orders?.order_items && selectedInvoice.orders.order_items.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Order Items</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {consolidateOrderItems(selectedInvoice.orders.order_items).map((item) => (
                          <tr key={item.id}>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                              {formatItemName(item)}
                              {item.variant_name && (
                                <span className="text-gray-500 dark:text-gray-400"> ({item.variant_name})</span>
                              )}
                              {item.item_description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{item.item_description}</p>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-white">
                              {item.quantity}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-white">
                              {formatCurrency(item.unit_price)}
                            </td>
                            <td className="px-4 py-2 text-sm text-right font-medium text-gray-900 dark:text-white">
                              {formatCurrency(item.total_price)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Order Summary */}
              {selectedInvoice.orders && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                        <span className="text-gray-900 dark:text-white">{formatCurrency(selectedInvoice.orders.subtotal || 0)}</span>
                      </div>
                      {selectedInvoice.orders.tax_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Tax:</span>
                          <span className="text-gray-900 dark:text-white">{formatCurrency(selectedInvoice.orders.tax_amount)}</span>
                        </div>
                      )}
                      {selectedInvoice.orders.service_charge > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Service Charge:</span>
                          <span className="text-gray-900 dark:text-white">{formatCurrency(selectedInvoice.orders.service_charge)}</span>
                        </div>
                      )}
                      {selectedInvoice.orders.discount_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Discount:</span>
                          <span className="text-red-600 dark:text-red-400">-{formatCurrency(selectedInvoice.orders.discount_amount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-semibold border-t border-gray-200 dark:border-gray-700 pt-2">
                        <span className="text-gray-900 dark:text-white">Total:</span>
                        <span className="text-gray-900 dark:text-white">{formatCurrency(selectedInvoice.orders.total_amount || 0)}</span>
                      </div>
                      {selectedInvoice.orders.payment_status && (
                        <div className="flex justify-between text-sm mt-2">
                          <span className="text-gray-600 dark:text-gray-400">Payment Status:</span>
                          <span className={`font-medium ${
                            selectedInvoice.orders.payment_status === 'paid' 
                              ? 'text-green-600 dark:text-green-400' 
                              : 'text-yellow-600 dark:text-yellow-400'
                          }`}>
                            {selectedInvoice.orders.payment_status.toUpperCase()}
                          </span>
                        </div>
                      )}
                      {selectedInvoice.orders.payment_method && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Payment Method:</span>
                          <span className="text-gray-900 dark:text-white capitalize">{selectedInvoice.orders.payment_method}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => handlePrintInvoice(selectedInvoice)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
              >
                <PrinterIcon className="h-5 w-5" />
                Print Invoice
              </button>
              <button
                onClick={() => {
                  setShowInvoiceModal(false)
                  setSelectedInvoice(null)
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Status Modal */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                {printModalType === 'success' ? (
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900">
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900">
                    <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
              </div>
              <h3 className={`text-lg font-semibold text-center mb-2 ${
                printModalType === 'success' 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {printModalType === 'success' ? 'Print Successful' : 'Print Failed'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                {printModalMessage}
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => setShowPrintModal(false)}
                  className={`px-6 py-2 rounded-md font-medium ${
                    printModalType === 'success'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Invoices

